<template>
  <div class="overview-page" :class="{ mobile: isMobile }">
    <Spinner :fullscreen="true" v-if="loadingData">
      <template v-slot:header
        >Track suspicious traffic seen coming from your network</template
      >
      We are loading your data.
      <br />This may take a few seconds
    </Spinner>

    <NormalHeaderLayout
      header="Suspicious traffic"
      :isWithoutBackground="true"
      :isAccessibleForAPNICStaff="true"
    >
      <div class="container" v-if="!isSuccessfulSummaryRequest"></div>
      <div class="container" v-else-if="noAccount">
        <empty-state
          title="Nothing to track here"
          description="Before we can track your suspicious traffic, we’ll first need to know what is applicable to your network."
        ></empty-state>
        <link-account class="mt-48"></link-account>
      </div>
      <div class="container" v-else-if="noASN">
        <empty-state
          title="No Autonomous System (AS) is associated to this member account."
          description="Currently only AS holders can track suspicious traffic coming from their network."
        ></empty-state>
      </div>
      <div class="container" v-else>
        <h2 class="page-title">
          Review suspicious traffic coming from your network
        </h2>
        <Notification />
        <TempNotification />
        <div class="tool-section">
          <div class="tool-section__left">
            <div>
              <MonthSelection />
            </div>
            <div>
              <ReportButton id="v-step-7" :disabled="noAccount"></ReportButton>
            </div>
          </div>
          <div class="tool-section__right">
            <DataSource />
          </div>
        </div>
        <div style="clear: both;"></div>

        <!-- Network at a glance section title and help accordion-->
        <SectionTitleAndHelpAccordion :isMobile="isMobile">
          <template slot="section-title"
            >Your network at a glance</template
          >
          <template slot="accordion-content">
            <p>
              This section presents the variation and total number of honeynet
              hits for the current and previous period coming from your network,
              economy and region.
            </p>
            <p>
              The period duration can be defined in the far-left combo box, in
              the above section.
            </p>
          </template>
        </SectionTitleAndHelpAccordion>

        <!-- Network at a glance -->
        <div
          class="card-wrapper"
          id="v-step-3"
          :class="welcomeTourMode ? 'example-watermark' : ''"
        >
          <StatsList></StatsList>
        </div>

        <!-- Network comparison section title and help accordion-->
        <SectionTitleAndHelpAccordion :isMobile="isMobile">
          <template slot="section-title"
            >Your network compared to {{ getNameByType(displayType) }}</template
          >
          <template slot="accordion-content">
            <p>
              This section displays a line chart comparing the evolution of
              honeynet hits in your network against your economy or region for
              the selected period.
            </p>
            <p>
              The period duration can be defined in the far-left combo box, in
              the top section.
            </p>
          </template>
        </SectionTitleAndHelpAccordion>

        <!-- Network comparison to economy / subregion -->
        <div
          class="card-wrapper"
          id="v-step-4"
          :class="welcomeTourMode ? 'example-watermark' : ''"
        >
          <ChartSection></ChartSection>
        </div>

        <!-- Top offending prefixes section title and help accordion-->
        <SectionTitleAndHelpAccordion :isMobile="isMobile">
          <template slot="section-title"
            >Your network top offending prefixes</template
          >
          <template slot="accordion-content">
            <p>
              This section lists the prefixes originating honeynet hits coming
              from your network.
            </p>
            <p>
              The list is sorted by the number of hits, and you can view the
              individual IP addresses and the type of attacks that caused the
              honeynet hits.
            </p>
          </template>
        </SectionTitleAndHelpAccordion>

        <!-- Top offending prefixes -->
        <div
          class="card-wrapper"
          id="v-step-5"
          :class="welcomeTourMode ? 'example-watermark' : ''"
        >
          <OffendingPrefixes
            class="overview-offending-prefixes"
          ></OffendingPrefixes>
        </div>
      </div>
    </NormalHeaderLayout>
    <ReportModal></ReportModal>
  </div>
</template>

<script>
import ReportButton from '../components/overview/report/ReportButton.vue';
import MonthSelection from '../components/MonthSelection';
import ChartSection from '../components/overview/ChartSection';
import Notification from '../components/overview/Notification';
import TempNotification from '../components/overview/TempNotification';
import DataSource from '../components/overview/DataSource';
import OffendingPrefixes from '../components/overview/OffendingPrefixes';
import StatsList from '../components/overview/stats/StatsList';
import Card from '../components/ui/Card';
import OverviewHeader from '../components/overview/OverviewHeader';
import ReportModal from '../components/overview/report/ReportModal';
import { mapActions, mapGetters, mapState } from 'vuex';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/ui/EmptyState.vue';
import LinkAccount from '../components/ui/LinkAccount.vue';
import NormalHeaderLayout from '../components/layout/NormalHeaderLayout.vue';
import mixin from '../utils/mixin';
import SectionTitleAndHelpAccordion from '../components/ui/SectionTitleAndHelpAccordion.vue';

export default {
  mixins: [mixin],
  components: {
    ReportButton,
    MonthSelection,
    ChartSection,
    Card,
    StatsList,
    OffendingPrefixes,
    OverviewHeader,
    Notification,
    TempNotification,
    DataSource,
    Spinner,
    ReportModal,
    EmptyState,
    LinkAccount,
    NormalHeaderLayout,
    SectionTitleAndHelpAccordion
  },
  computed: {
    ...mapState(['isSuccessfulSummaryRequest']),
    ...mapGetters(['isMobile']),
    ...mapGetters('dataStore', [
      'welcomeTourMode',
      'loadingData',
      'noAccount',
      'noASN',
      'selectedASN'
    ])
  },
  methods: {
    ...mapActions('dataStore', ['selectASN'])
  },
  mounted() {
    const asnId = this.selectedASN && this.selectedASN.id;
    asnId && this.selectASN(asnId);
  }
};
</script>

<style lang="scss" scoped>
.overview-page {
  background-color: #f5f5f7;

  .page-title {
    margin-bottom: 1.5rem;
  }

  .tool-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    &__left {
      flex: 70%;
      display: flex;
      flex-wrap: wrap;

      div:nth-child(1) {
        margin-right: 0.5rem;
      }
    }

    &__right {
      flex: 30%;
      display: flex;
    }
  }
}

.card-wrapper {
  background-color: #fff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
  padding: 20px;
  margin-bottom: 15px;

  @media screen and (max-width: 485px) {
    padding: 10px;

    &.example-watermark {
      padding: 20px 10px;
    }
  }
}
.gap {
  height: 90px;

  @media screen and (max-width: 425px) {
    height: 45px;
  }
}

.overview-offending-prefixes /deep/ {
  .activity-table {
    min-height: 0;
  }
  & .ivu-table-wrapper {
    .ivu-table {
      &-header {
        background-color: #e6e6e6;
      }
    }
  }
}

.example-watermark {
  position: relative;
  &::before {
    content: 'This data is an example only';
    position: absolute;
    top: 4px;
    left: 20px;
    font-size: 18px;
    opacity: 0.2;
    font-weight: 700;
    text-transform: uppercase;

    @media screen and (max-width: 485px) {
      top: 0px;
      left: 10px;
    }
  }
}

.mt-48 {
  margin-top: 48px;
}

.mobile.overview-page {
  .container {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
